import React from 'react';
import FormRadioButton from '../../baseComponents/RadioButton';
import FormRadioGroup from '../../baseComponents/Radiogroup';
import withSideBar from '../../../assets/withSideBarIcon.svg';
import withoutSideBar from '../../../assets/withoutSideBarIcon.svg';
import chatIcon from '../../../assets/chatIcon.svg';
import userIcon from '../../../assets/userIcon.svg';
import callIcon from '../../../assets/call-Icon.svg';
import groupIcon from '../../../assets/groupIcon.svg';
import './style.css';
import CheckBoxCard from '../../baseComponents/CheckBoxCard';
import RadioButtonCard from '../../baseComponents/RadioButtonCard';
import type { FormInstance } from 'antd/es/form';
import { useBuilderSettingContext } from '../../../sampleApp/src/context/BuilderSettingsContext';

/**
 * Props for the LayoutComponent.
 * @typedef {Object} LayoutComponentProps
 * @property {FormInstance} form - The Ant Design form instance.
 */
interface LayoutComponentProps {
  form: FormInstance;
}

/**
 * LayoutComponent handles the layout settings of the application.
 * Users can configure sidebar visibility, tabs, and chat types.
 *
 * @param {LayoutComponentProps} props - The component props.
 * @returns {JSX.Element | null} The rendered component or null if layoutFeatures is undefined.
 */
const LayoutComponent = ({ form }: LayoutComponentProps) => {
  const { layoutFeatures, setLayoutFeatures } = useBuilderSettingContext();

  /**
   * Handles the change of sidebar visibility.
   * @param {boolean} value - Whether the sidebar is enabled.
   */
  const handleWithSideBarChange = (value: boolean) => {
    setLayoutFeatures((prevFeatures) => ({
      ...prevFeatures,
      withSideBar: value,
    }));
  };

  /**
   * Toggles the selected tab in the layout.
   * @param {string} tabName - The name of the tab to toggle.
   */
  const handleTabToggle = (tabName: string) => {
    setLayoutFeatures((prevFeatures) => {
      let newTabs = [...prevFeatures.tabs];

      if (newTabs.includes(tabName)) {
        newTabs = newTabs.filter((tab) => tab !== tabName);
      } else {
        newTabs.push(tabName);
      }

      const currentLayout = form.getFieldValue('layout') || {};
      form.setFieldsValue({
        layout: {
          ...currentLayout,
          tabs: newTabs,
        },
      });

      return {
        ...prevFeatures,
        tabs: newTabs,
      };
    });
  };

  /**
   * Handles radio button selection for chat type.
   * @param {string} value - The selected chat type.
   */
  const handleRadioButtonChange = (value: string) => {
    form.setFieldsValue({
      layout: {
        chatType: value,
      },
    });

    setLayoutFeatures((prevFeatures) => ({
      ...prevFeatures,
      chatType: value,
    }));
  };

  const tabCards = (
    <>
      {layoutFeatures && layoutFeatures?.withSideBar ? (
        <>
          <div className="tabsTitle">Tabs</div>
          <CheckBoxCard
            icon={<img className="tabIcon" src={chatIcon} alt="chat Icon" />}
            title="Conversations"
            description="View all your chats in one place"
            onChange={() => handleTabToggle('chats')}
            isFeatureEnabled={layoutFeatures && layoutFeatures.tabs?.includes('chats')}
            disabled={layoutFeatures.tabs?.includes('chats') && layoutFeatures.tabs.length === 1}
          />
          <CheckBoxCard
            icon={<img src={callIcon} alt="call log icon" className="tabIcon" />}
            title="Call Logs"
            description="View recent call history and details"
            onChange={() => handleTabToggle('calls')}
            isFeatureEnabled={layoutFeatures && layoutFeatures.tabs?.includes('calls')}
            disabled={layoutFeatures.tabs?.includes('calls') && layoutFeatures.tabs.length === 1}
          />
          <CheckBoxCard
            icon={<img src={userIcon} alt="users Icon" className="tabIcon" />}
            title="Users"
            description="Access and connect with users"
            onChange={() => handleTabToggle('users')}
            isFeatureEnabled={layoutFeatures && layoutFeatures.tabs?.includes('users')}
            disabled={layoutFeatures.tabs?.includes('users') && layoutFeatures.tabs.length === 1}
          />
          <CheckBoxCard
            icon={<img src={groupIcon} alt="group Icon" className="tabIcon" />}
            title="Groups"
            description="Access & create group conversations"
            onChange={() => handleTabToggle('groups')}
            isFeatureEnabled={layoutFeatures && layoutFeatures.tabs?.includes('groups')}
            disabled={layoutFeatures.tabs?.includes('groups') && layoutFeatures.tabs.length === 1}
          />
        </>
      ) : (
        <>
          <div className="chatTypeTitleWrapper">
            <div className="chatTypeTitle">Chat Type</div>
            <div className="chatTypeDescription">Choose how you’d like to communicate.</div>
          </div>

          <FormRadioGroup name={['layout', 'chatType']}>
            <div className="chatRadioWrapper" id="radioWrapper">
              <RadioButtonCard
                onChange={() => handleRadioButtonChange('user')}
                icon={<img src={userIcon} alt="user Icon" className="tabIcon" />}
                title="User Chat"
                description="One-on-one private messaging."
                value="user"
              />
              <RadioButtonCard
                icon={<img src={groupIcon} alt="group Icon" className="tabIcon" />}
                title="Group Chats"
                description="Messaging with multiple participants."
                value="group"
                onChange={() => handleRadioButtonChange('group')}
              />
            </div>
          </FormRadioGroup>
        </>
      )}
    </>
  );

  if (!layoutFeatures) {
    return null;
  }

  return (
    <div id="layoutFeatureWrapper" className="layoutFeatureWrapper" style={{ width: '100%' }}>
      <div className="featureHeader layoutFeaturesHeader">Layout</div>
      <div className="layoutDescriptions">
        <div className="descriptionTitle">Sidebar</div>
        <div className="descriptionBody">Quick access to conversation and calls</div>
      </div>
      <div className="sideBarOptionsContainer">
        <FormRadioGroup
          name={['layout', 'withSideBar']}
          rules={[
            {
              required: true,
              message: 'Please select whether to enable the sidebar',
            },
          ]}
        >
          <div className="radioWrapper" id="radioWrapper">
            <FormRadioButton
              id="withSideBar"
              icon={<img src={withSideBar} alt="With Sidebar" />}
              label="With Sidebar"
              value={true}
              onClick={() => handleWithSideBarChange(true)}
            />
            <FormRadioButton
              icon={<img src={withoutSideBar} alt="Without Sidebar" />}
              label="Without Sidebar"
              value={false}
              onClick={() => handleWithSideBarChange(false)}
            />
          </div>
        </FormRadioGroup>
      </div>
      <div className="tabsOptionWrapper">{tabCards}</div>
    </div>
  );
};

export default LayoutComponent;
